// TODO (Noah, 2024-10-09): Re-enable this rule
/* eslint-disable replo/consistent-component-exports */
import type { UseApplyComponentActionType } from "@v1/hooks/useApplyComponentAction";
import type {
  ImportWarning,
  ProgressStep,
} from "@v1/reducers/figma-to-replo-reducer";
import type { EditorRootState } from "@v1/store/store";

import * as React from "react";

import { addListener } from "@reduxjs/toolkit";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken, useMutation } from "@tanstack/react-query";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { LEFT_BAR_WIDTH } from "@v1/components/editor/constants";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import { useV1ErrorToast } from "@v1/hooks/useV1ErrorToast";
import {
  selectDraftElement_warningThisWillRerenderOnEveryUpdate,
  selectIsShopifyIntegrationEnabled,
  setDraftElement,
  undoOperation,
} from "@v1/reducers/core-reducer";
import {
  reset as resetFigmaState,
  selectIsOpenPanel,
  selectProgress,
  selectWarnings,
  setPanelVisibility,
} from "@v1/reducers/figma-to-replo-reducer";
import {
  useEditorDispatch,
  useEditorSelector,
  useEditorStore,
} from "@v1/store/store";
import { storeRouter } from "@v1/trpc/routes/store";
import { getTokenFromStorage } from "@v2/features/infra/auth";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { defaultFetchCredentials, getPublisherUrl } from "@v2/utils/env";
import {
  Check,
  ChevronDown,
  CircleAlert,
  CircleHelp,
  Info,
  Upload,
  X,
} from "lucide-react";
import { forEachComponentAndDescendants } from "replo-runtime";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { pluralize } from "replo-utils/lib/pluralize";
import { ReploError } from "schemas/errors";
import { z } from "zod";

class ShopifyAssetUploadError extends ReploError {
  constructor(message: string, options?: { cause?: unknown }) {
    super({ message, cause: options?.cause });
  }
}

const shopifyAssetUploadResultSchema = z.object({
  asset: z.object({
    contentType: z.string(),
    id: z.string(),
    publicUrl: z.string(),
  }),
});

class UploadFontError extends ReploError {
  constructor(message: string, additionalData: Record<string, unknown>) {
    super({ message, additionalData });
  }
}

const FigmaImportLoadingAnimation = ({
  isAnimated = true,
}: {
  isAnimated: boolean;
}) => {
  const style = {
    "--cube-color": "var(--color-blue-400)",
    "--cube-before-color": "var(--color-blue-600)",
    "--cube-after-color": "var(--color-blue-800)",
    "--animationPlayState": isAnimated ? "running" : "paused",
  } as React.CSSProperties;
  return (
    <div className="relative m-[2em] h-[84px] w-[84px]" style={style}>
      <div className="replo-cubes-loading-box">
        <div className="replo-cubes-loading-cube one"></div>
        <div className="replo-cubes-loading-cube two"></div>
        <div className="replo-cubes-loading-cube three"></div>
      </div>
    </div>
  );
};

const progressStepsMap: Record<ProgressStep, string> = {
  initializing: "Initializing",
  uploadingImages: "Uploading Assets",
  importComplete: "Import completed! ✅",
};

const MissingFontRow: React.FC<{
  fontFamily: string;
  uploadAndReplaceFont: (font: File, fontFamily: string) => Promise<void>;
}> = ({ fontFamily, uploadAndReplaceFont }) => {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const errorToast = useV1ErrorToast();
  // Note (Evan, 2024-08-07): Maintain state independent of the RTK isLoading here
  // since we do some additional processing after the RTK upload is complete to
  // actually set the font
  const [requestState, setRequestState] = React.useState<
    "preRequest" | "loading" | "complete"
  >("preRequest");

  const isShopifyIntegrationEnabled = useEditorSelector(
    selectIsShopifyIntegrationEnabled,
  );

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    const fontFile = event.target.files && event.target.files[0];
    if (!fontFile) {
      return;
    }

    setRequestState("loading");

    void uploadAndReplaceFont(fontFile, fontFamily)
      .then(() => {
        setRequestState("complete");
      })
      .catch(() => {
        errorToast(
          "Error",
          `An error occurred uploading ${fontFamily}. Please try again or contact support.`,
          "error.figma.font.upload",
          {
            fontFamily,
          },
        );
        setRequestState("preRequest");
      });
  };

  const isLoading = requestState === "loading";

  return (
    <div className="flex flex-row items-center justify-between gap-1">
      {fontFamily}
      {isShopifyIntegrationEnabled &&
        (requestState !== "complete" ? (
          <div className="w-[72px] shrink-0">
            <DeprecatedButton
              variant="secondary"
              size="sm"
              onClick={() => {
                fileInputRef.current?.click();
              }}
              isLoading={isLoading}
              disabled={isLoading}
            >
              <div className="flex flex-row items-center gap-1">
                <Upload size={12} />
                <span className="text-muted">Upload</span>
              </div>
            </DeprecatedButton>
            <input
              type="file"
              ref={fileInputRef}
              accept=".woff,.woff2"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        ) : (
          <div className="text-emerald flex w-[72px] shrink-0 items-center justify-center">
            <Check size={24} />
          </div>
        ))}
    </div>
  );
};

const CollapsibleWarningDetails: React.FC<{
  icon: React.ReactNode;
  title: string;
  content: React.ReactNode;
  shouldPreventOpenAndClose?: boolean;
}> = ({ icon, title, content, shouldPreventOpenAndClose }) => {
  const [isMenuOpen, setMenuOpen] = React.useState(false);
  return (
    <div className="transition-all">
      <div className="flex flex-row items-center justify-between text-xs">
        <div className="flex flex-row items-center gap-2">
          {icon}
          <span className="text-default text-sm font-semibold">{title}</span>
        </div>
        <button
          onClick={() => {
            if (!shouldPreventOpenAndClose) {
              setMenuOpen(!isMenuOpen);
            }
          }}
        >
          <ChevronDown
            size={12}
            className={twMerge(
              "text-muted transition-all",
              isMenuOpen && "rotate-180",
            )}
          />
        </button>
      </div>
      {isMenuOpen && <div className="mt-2 px-2">{content}</div>}
    </div>
  );
};

const MissingFontsWarningDetails: React.FC<{ missingFonts: string[] }> = ({
  missingFonts,
}) => {
  // Note (Evan, 2024-08-07): We don't want to allow closing the menu
  // once an upload has begun
  const [hasUploadBegun, setHasUploadBegun] = React.useState(false);

  const { mutateAsync: uploadShopifyAsset } = useMutation({
    mutationFn: async ({
      file,
      projectId,
      filename,
      displayName,
    }: {
      file: File;
      projectId: string;
      filename: string;
      displayName?: string;
    }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("storeId", projectId);
      formData.append("filename", filename);
      formData.append("sourceType", "files");
      if (displayName) {
        formData.append("displayName", displayName);
      }

      const token = getTokenFromStorage();
      const response = await fetch(
        `${getPublisherUrl()}/api/v1/shopify/assets`,
        {
          method: "PUT",
          headers: token ? { Authorization: `Token ${token}` } : {},
          credentials: defaultFetchCredentials,
          body: formData,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new ShopifyAssetUploadError(
          errorData.message ?? "Failed to upload asset to Shopify",
          { cause: errorData },
        );
      }

      const json: unknown = await response.json();
      const parsed = shopifyAssetUploadResultSchema.safeParse(json);

      if (!parsed.success) {
        throw new ShopifyAssetUploadError(
          "Invalid response from Shopify asset upload",
          { cause: parsed.error },
        );
      }

      return parsed.data;
    },
  });
  const projectId = useCurrentProjectId();
  const { refetch: refetchUploadedFonts } = storeRouter.getFonts.useQuery(
    projectId ? { storeId: projectId } : skipToken,
  );
  const applyComponentAction = useApplyComponentAction();
  const isShopifyIntegrationEnabled = useEditorSelector(
    selectIsShopifyIntegrationEnabled,
  );

  const store = useEditorStore();

  const pluralFonts = pluralize({
    singular: "font",
    plural: "fonts",
    count: missingFonts.length,
  });

  const message = `${missingFonts.length} missing ${pluralFonts}`;

  const uploadAndReplaceFont = React.useCallback(
    async (font: File, fontFamily: string) => {
      setHasUploadBegun(true);
      await uploadShopifyAsset({
        file: font,
        filename: font.name,
        projectId: projectId ?? "",
        displayName: fontFamily,
      });

      const { data: uploadedFonts } = await refetchUploadedFonts({
        throwOnError: true,
      });

      if (!uploadedFonts) {
        // eslint-disable-next-line replo/no-raw-error-throw
        throw new Error("Error fetching fonts");
      }

      const uploadedFont = uploadedFonts.find(
        (font) => font.displayName === fontFamily,
      );

      if (!uploadedFont) {
        throw new UploadFontError("Error fetching uploaded font", {
          fontFamily,
          uploadedFonts,
        });
      }

      // Note (Evan, 2024-08-06): Set all components with the given font
      const draftElement =
        selectDraftElement_warningThisWillRerenderOnEveryUpdate(
          store.getState(),
        );
      if (!draftElement) {
        throw new UploadFontError(
          "Error, no draft element selected, this case should not be reachable",
          { fontFamily, uploadedFonts },
        );
      }

      const actions: UseApplyComponentActionType[] = [];
      forEachComponentAndDescendants(draftElement.component, (component) => {
        if (
          component.type === "text" &&
          component.props.style?.fontFamily === fontFamily
        ) {
          actions.push({
            componentId: component.id,
            type: "setStyles",
            value: {
              fontFamily: uploadedFont.name,
            },
          });
        }
      });

      applyComponentAction({
        type: "applyCompositeAction",
        value: actions,
      });
    },
    [
      uploadShopifyAsset,
      projectId,
      refetchUploadedFonts,
      store,
      applyComponentAction,
    ],
  );

  return (
    <CollapsibleWarningDetails
      icon={<CircleHelp size={16} className="text-default" />}
      title={message}
      content={
        <div className="text-muted flex max-h-[160px] flex-col gap-2 overflow-y-scroll text-sm">
          {!isShopifyIntegrationEnabled && (
            <div className="text-default text-xs font-medium">
              Connect a Shopify store to upload fonts.
            </div>
          )}
          {missingFonts.map((fontFamily) => (
            <MissingFontRow
              fontFamily={fontFamily}
              key={fontFamily}
              uploadAndReplaceFont={uploadAndReplaceFont}
            />
          ))}
        </div>
      }
      shouldPreventOpenAndClose={hasUploadBegun}
    />
  );
};

const MultipleFillsWarningDetails: React.FC<{ nodeNames: string[] }> = ({
  nodeNames,
}) => {
  const message =
    nodeNames.length === 1
      ? `Layer "${nodeNames[0]}" has multiple fills. Only the top fill has been imported.`
      : `${nodeNames.length} layers have multiple fills. Only the top fill for each layer has been imported.`;

  return (
    <CollapsibleWarningDetails
      icon={<Info size={16} className="text-primary shrink-0" />}
      title="Multiple fills detected"
      content={<div className="text-muted text-xs">{message}</div>}
    />
  );
};

const MaskWarningDetails: React.FC<{ nodeNames: string[] }> = ({
  nodeNames,
}) => {
  const message =
    nodeNames.length === 1
      ? `Layer "${nodeNames[0]}" was not imported.`
      : `${nodeNames.length} layers were not imported.`;

  return (
    <CollapsibleWarningDetails
      icon={<Info size={16} className="text-primary shrink-0" />}
      title="Mask detected"
      content={<div className="text-muted text-xs">{message}</div>}
    />
  );
};

const ImageUploadWarningDetails: React.FC<{ reploComponentIds: string[] }> = ({
  reploComponentIds,
}) => {
  const message = `Error uploading ${reploComponentIds.length} images`;
  return (
    <div className="flex flex-row items-center gap-2">
      <CircleAlert size={16} className="text-rose shrink-0" />
      <span className="text-default text-sm font-semibold">{message}</span>
    </div>
  );
};

const WarningDetails: React.FC<{ warning: ImportWarning }> = ({ warning }) => {
  return exhaustiveSwitch(warning)({
    missingFonts: ({ fonts }) => (
      <MissingFontsWarningDetails missingFonts={fonts} />
    ),
    multipleFills: ({ nodeNames }) => (
      <MultipleFillsWarningDetails nodeNames={nodeNames} />
    ),
    mask: ({ nodeNames }) => <MaskWarningDetails nodeNames={nodeNames} />,
    uploadError: ({ reploComponentIds }) => (
      <ImageUploadWarningDetails reploComponentIds={reploComponentIds} />
    ),
  });
};

const FigmaImportPanelWrapper = () => {
  const isOpenPanel = useEditorSelector(selectIsOpenPanel);
  return isOpenPanel && <FigmaImportPanelContent />;
};

const FigmaImportPanelContent = () => {
  const dispatch = useEditorDispatch();
  const progress = useEditorSelector(selectProgress);
  const warnings = useEditorSelector(selectWarnings);
  const logEvent = useLogAnalytics();

  const importInProgress = !["importComplete", "importCancelled"].includes(
    progress,
  );

  const onClose = React.useCallback(() => {
    dispatch(setPanelVisibility(false));
    dispatch(resetFigmaState());
  }, [dispatch]);

  // Note (Evan, 2024-08-23): Add a listener here that will close the panel
  // when the draft element changes (since it's no longer relevant)
  React.useEffect(() => {
    const unsubscribe = dispatch(
      addListener({
        predicate: (action, _currentState, _previousState) => {
          if (!setDraftElement.match(action)) {
            return false;
          }

          // Note (Evan, 2024-08-23): Not sure why TS isn't inferring
          // these types here ...
          const currentState = _currentState as EditorRootState;
          const previousState = _previousState as EditorRootState;
          return (
            currentState.core.elements.draftElementId !==
            previousState.core.elements.draftElementId
          );
        },
        effect: () => {
          onClose();
        },
      }),
    );
    return unsubscribe as unknown as () => void;
  }, [dispatch, onClose]);

  const onConfirm = () => {
    logEvent("editor.paste.figma.confirm", {});
    onClose();
  };

  const onCancel = () => {
    logEvent("editor.paste.figma.cancel", {});
    dispatch(undoOperation());
    onClose();
  };

  return (
    <div
      className="fixed inline-flex w-64 flex-col gap-2 self-stretch rounded-lg bg-white p-4 shadow-sm"
      style={{
        left: LEFT_BAR_WIDTH + 14,
        top: 124,
      }}
    >
      <div className="flex items-center justify-between">
        <p className="text-bold text-sm">Figma Import</p>
        <DeprecatedIconButton
          variant="tertiary"
          icon={<X size={16} />}
          tooltipText="Close panel"
          onClick={onConfirm}
        />
      </div>
      <div className="flex h-[208px] flex-col justify-between">
        <div className="flex items-center justify-center">
          {importInProgress ? (
            <FigmaImportLoadingAnimation isAnimated={importInProgress} />
          ) : (
            <img
              className="h-[154px] w-[130px]"
              src="/images/figma/import-complete.svg"
              alt="Blue and white megaphone with confetti"
            />
          )}
        </div>
        <div className="flex flex-col gap-2 p-2">
          <div className="text-primary text-center text-sm font-semibold">
            {progressStepsMap[progress]}
          </div>
          {importInProgress && (
            <div className="text-muted text-center text-xs font-medium">
              This may take a moment
            </div>
          )}
        </div>
      </div>

      {warnings.length > 0 && (
        <div className="bg-sheet mb-2 flex flex-col gap-3 rounded-sm p-2">
          {warnings.map((warning, i) => (
            <WarningDetails warning={warning} key={i} />
          ))}
        </div>
      )}
      <DeprecatedButton
        onClick={onConfirm}
        variant="primary"
        size="base"
        data-testid="looks-good-button"
        tooltipText={importInProgress ? undefined : "Keep the imported design"}
        aria-label="Looks good"
        isLoading={importInProgress}
        disabled={importInProgress}
        layoutClassName="w-full"
      >
        Looks Great!
      </DeprecatedButton>
      <DeprecatedButton
        onClick={onCancel}
        variant="secondary"
        size="base"
        disabled={importInProgress}
        data-testid="figma-cancel-button"
        tooltipText="Undo the import"
        aria-label="Cancel"
        layoutClassName="w-full"
      >
        Cancel
      </DeprecatedButton>
    </div>
  );
};

export default FigmaImportPanelWrapper;
